/**
 * Withdrawals (partners, agencies and customers), admin side.
 *
 * Reads go straight to Firestore (admins may read withdrawals/). Every change
 * goes through adminResolveWithdrawal, which moves the wallet money in the same
 * transaction as the status and tells the partner.
 */
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

export type WithdrawalStatus = 'pending' | 'processing' | 'failed' | 'paid' | 'rejected';
export type WithdrawalAction = 'approve' | 'mark_paid' | 'reject' | 'payout' | 'refresh';
export type PayMethod = 'bank_transfer' | 'upi' | 'other';

export interface AdminWithdrawal {
  id: string;
  uid: string;
  kind: 'individual' | 'agency' | 'customer';
  payeeName: string;
  agencyId?: string | null;
  cityId?: string | null;
  amount: number;
  status: WithdrawalStatus;
  destination: { accountHolderName: string; accountNumber: string; ifsc: string };
  attempt?: number;
  payoutId?: string | null;
  payoutStatus?: string | null;
  payoutMode?: string | null;
  paidVia?: 'manual' | 'razorpayx';
  /** Manual flow: approved by an admin and being paid outside the app. */
  manual?: boolean;
  approvedByName?: string;
  payMethod?: PayMethod;
  paidByName?: string;
  paidNote?: string;
  utr?: string | null;
  rejectionReason?: string | null;
  lastError?: string | null;
  history: Array<{ at: Date | null; status: string; by: string; note: string }>;
  createdAt: Date | null;
  paidAt: Date | null;
}

export interface PayoutConfig {
  razorpayx: boolean;
  webhook: boolean;
  /** Partner & agency minimum. */
  minimum: number;
  customerMinimum: number;
}

const toDate = (v: any): Date | null => (v?.toDate ? v.toDate() : null);

const resolveFn = httpsCallable<{ withdrawalId: string; action: WithdrawalAction; utr?: string; method?: PayMethod; reason?: string; note?: string }, { status: WithdrawalStatus; payoutStatus?: string; note?: string }>(functions, 'adminResolveWithdrawal');
const configFn = httpsCallable<void, PayoutConfig>(functions, 'withdrawalPayoutConfig');

class WithdrawalAdminService {
  /** Newest first. A city admin sees only their city's. */
  async list(cityId: string | null, max = 300): Promise<AdminWithdrawal[]> {
    const snap = await getDocs(query(collection(db, 'withdrawals'), orderBy('createdAt', 'desc'), limit(max)));
    return snap.docs
      .map((d) => {
        const w = d.data() as any;
        return {
          ...w,
          id: d.id,
          history: (w.history || []).map((h: any) => ({ ...h, at: toDate(h.at) })),
          createdAt: toDate(w.createdAt),
          paidAt: toDate(w.paidAt),
        } as AdminWithdrawal;
      })
      .filter((w) => !cityId || w.cityId === cityId);
  }

  async resolve(withdrawalId: string, action: WithdrawalAction, extra: { utr?: string; method?: PayMethod; reason?: string; note?: string } = {}) {
    return (await resolveFn({ withdrawalId, action, ...extra })).data;
  }

  async config(): Promise<PayoutConfig> {
    return (await configFn()).data;
  }
}

export default new WithdrawalAdminService();
